
/**
 * Klasse für interne Verwendung von TPCache.
 */
class TPCacheObject {
    constructor(value, ttl = 0) {
        this.t = 'TPCacheObject'
        this.v = value;
        this.e = ttl ? (Date.now() + 1000 * ttl) : 0; // expiry timestamp
    }

    get expired() {
        return (this.e > 0 && this.e < Date.now());
    }

    get value() {
        return this.v;
    }

    static reviver(key, value) {
        if ((typeof value === 'object')
            && (value !== null)
            && ('t' in value)
            && (value.t === 'TPCacheObject')
        ) {
            const cacheObject = new TPCacheObject(value.v);
            cacheObject.e = value.e;
            return cacheObject;
        }
        return value;
    }
}

export default class TPCache {
    /**
     * Erzeugt einen Cache Bucket für den gegebenen Namespace
     * Namespace = prefix für localStorage keys
     *
     * @param {string} [namespace]
     * @param {number} [ttl] Standard-ttl für alle Cache-Objekte
     */
    constructor(namespace = 'tp_cache_', ttl = 0) {
        this.namespace = namespace;
        this.ttl = ttl;
    }

    /**
     * Prüft ob das Cache-Item vorhanden und nicht abgelaufen ist
     * @param {string} key
     * @returns {bool}
     */
    has(key) {
        return this.get(key) !== null;
    }

    /**
     * Speichert einen neuen Wert im Cache
     * @param {string} key
     * @param {any} value
     * @param {number} ttl Optional. 0: läuft nie ab.
     * @returns {TPCache};
     */
    set(key, value, ttl = null) {
        if (ttl === null) {
            ttl = this.ttl;
        }
        const cacheObject = new TPCacheObject(value, ttl);
        localStorage.setItem(this.namespace + key, JSON.stringify(cacheObject));
        return this;
    }

    /**
     * Gibt einen Wert aus dem Cache zurück
     * @param {string} key
     * @returns {any|null} null wenn das Item nicht gefunden wurde oder abgelaufen ist
     */
    get(key) {
        const raw = localStorage.getItem(this.namespace + key);
        if (raw === null) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw, TPCacheObject.reviver);
            if (parsed instanceof TPCacheObject) {
                if (parsed.expired) {
                    this.remove(key);
                } else {
                    return parsed.value;
                }
            }
        } catch (e) {
            console.error("CACHE PARSE FAIL", e, raw);
        }
        return null;
    }

    /**
     * Löscht den Wert im Cache
     * @param {string} key
     * @returns {TPCache}
     */
    remove(key) {
        localStorage.removeItem(this.namespace + key);
        return this;
    }

    /**
     * Holt alle Werte aus dem Cache
     * @returns {Object.<string, any>}
     */
    getAll() {
        const entries = Object.keys(localStorage)
            .filter(key => key.substr(0, this.namespace.length) === this.namespace)
            .map(namespaced_key => {
                const key = namespaced_key.substr(this.namespace.length);
                console.log("CACHE MAP", namespaced_key, key);
                return [key, this.get(key, false)];
            })
            .filter(entry => entry[1] !== null);
        return Object.fromEntries(entries);
    }

    /**
     * Löscht alle abgelaufenen Objekte aus dem Cache
     * @returns {TPCache}
     */
    collectGarbage() {
        this.getAll();
        return this;
    }

    /**
     * Löscht alle Objekte aus dem Cache
     * @returns {TPCache}
     */
    clear() {
        Object.keys(this.getAll()).forEach(key => this.remove(key));
        return this;
    }
}

